import { useCallback, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { Scan, Share2, Copy, FileImage, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { loadConfig } from '@/lib/config';
import { loadImage, canvasToBlob, downloadBlob, copyCanvasToClipboard } from '@/lib/shareExport';

/**
 * Quick-access "Show AR Marker" modal — lets a presenter pull up just the
 * marker image on their own screen (e.g. to hold up to a camera, or share
 * with someone directly) without switching to the separate audience view.
 */
export function MarkerDialog() {
  const [open, setOpen] = useState(false);
  const [markerURL, setMarkerURL] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [exporting, setExporting] = useState(false);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && !markerURL) {
      loadConfig()
        .then((cfg) => setMarkerURL(`${cfg.syncBaseURL}/assets/markers/CiscoARMarker.png`))
        .catch(() => setLoadFailed(true));
    }
  };

  const buildMarkerCanvas = useCallback(async (): Promise<HTMLCanvasElement> => {
    if (!markerURL) throw new Error('Marker not loaded yet');
    const img = await loadImage(markerURL, 'anonymous');
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not supported in this browser');
    ctx.drawImage(img, 0, 0);
    return canvas;
  }, [markerURL]);

  const withExporting = useCallback(async (fn: () => Promise<void>) => {
    setExporting(true);
    try {
      await fn();
    } catch (err) {
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Something went wrong generating the image.',
      });
    } finally {
      setExporting(false);
    }
  }, []);

  const handleCopy = () => withExporting(async () => {
    const canvas = await buildMarkerCanvas();
    await copyCanvasToClipboard(canvas);
    toast.success('Copied to clipboard', { description: 'Paste it into Slack, email, or wherever you need it.' });
  });

  const handleDownloadJPEG = () => withExporting(async () => {
    const canvas = await buildMarkerCanvas();
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
    downloadBlob(blob, 'casper-ar-marker.jpg');
  });

  const handleDownloadPDF = () => withExporting(async () => {
    const canvas = await buildMarkerCanvas();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({
      orientation: canvas.width >= canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height);
    pdf.save('casper-ar-marker.pdf');
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Scan className="h-3.5 w-3.5" /> Show AR Marker
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>AR Marker</DialogTitle>
          <DialogDescription>
            Show this on your screen for someone to point their camera at, or share it with them directly.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[34rem] items-center justify-center rounded-xl bg-white p-6">
          {markerURL ? (
            <img src={markerURL} alt="Cisco AR marker" className="h-[30rem] w-[30rem] object-contain" />
          ) : loadFailed ? (
            <p className="max-w-xs text-center text-sm text-slate-500">
              Couldn&apos;t load the marker. Check the sync server is reachable and try again.
            </p>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          )}
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="outline" size="sm" className="w-full" disabled={!markerURL || exporting}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              Share
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="center"
              side="bottom"
              sideOffset={8}
              className="z-50 min-w-[14rem] rounded-md border bg-card p-1 text-card-foreground shadow-md"
            >
              <DropdownMenu.Item
                onSelect={handleCopy}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy to clipboard
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={handleDownloadJPEG}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground"
              >
                <FileImage className="h-3.5 w-3.5" />
                Download as JPEG
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={handleDownloadPDF}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground"
              >
                <FileText className="h-3.5 w-3.5" />
                Download as PDF
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </DialogContent>
    </Dialog>
  );
}
